import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Fab,
  Divider,
  Chip,
} from '@mui/material';
import { Save as SaveIcon, Search as SearchIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import config from '../config';
import { useRole } from '../contexts/RoleContext';

const InventoryManagementPage = () => {
  // ✅ ALL HOOKS AT THE TOP - BEFORE ANY CONDITIONAL LOGIC
  const userRole = useRole();
  
  // State management
  const [circuitId, setCircuitId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [circuitData, setCircuitData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Access control configuration
  const INVENTORY_ADMIN_ACCESS = {
    requiredRole: 'ODM Admin',
    allowedUsers: [
      'akshay.deshmukh@allianz.com',
      'ankita.singh@allianz.com',
      'jane.smith@allianz.com',
      'admin@allianz.com',
     'retheesh_olangattu@allianz.com',
      'admin@allianz.com',
      'ALIN-MARIAN.DAVID@ALLIANZ.COM'
    ]
  };

  // Access check function
  const hasInventoryAccess = () => {
    const isODMAdmin = userRole.roleName === 'ODM Admin';
    const isSpecificUser = INVENTORY_ADMIN_ACCESS.allowedUsers.includes(userRole.userEmail);
    return isODMAdmin && isSpecificUser;
  };

  // ✅ CONDITIONAL RETURN AFTER ALL HOOKS
  if (!hasInventoryAccess()) {
    return (
      <Layout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" color="error" gutterBottom>
                Access Denied - Inventory Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This feature is restricted to ODM Admins with specific authorization.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current user: {userRole.userEmail} ({userRole.roleName})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please contact your administrator for access.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="primary">
                  Authorized for: Circuit inventory updates, Excel database management
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Layout>
    );
  }

  // Search function
  const handleSearch = async () => {
    if (!circuitId.trim()) {
      setError('Please enter a Circuit ID');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setCircuitData(null);
      
      const response = await fetch(`${config.apiUrl}/api/inventory/search/${encodeURIComponent(circuitId.trim())}`);
      const result = await response.json();
      
      if (result.success) {
        setCircuitData(result.circuit_data);
        
        // Initialize form values
        const initialValues = {};
        Object.keys(result.circuit_data).forEach(key => {
          if (key !== 'circuit_id') {
            const fieldData = result.circuit_data[key];
            if (fieldData && typeof fieldData === 'object') {
              initialValues[key] = fieldData.value || '';
            }
          }
        });
        
        setFormValues(initialValues);
        setHasChanges(false);
      } else {
        setError(result.error);
      }
      
    } catch (err) {
      setError(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Value change handler
  const handleValueChange = (fieldKey, value) => {
    setFormValues(prev => ({ ...prev, [fieldKey]: value }));
    setHasChanges(true);
    setError(null);
  };

  // Save function
  const handleSave = async () => {
    if (!hasChanges) {
      setError('No changes detected to save');
      return;
    }
    
    if (!circuitData) {
      setError('No circuit data loaded');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Prepare update data (only editable fields that have changed)
      const updateData = {};
      Object.keys(formValues).forEach(fieldKey => {
        const fieldData = circuitData[fieldKey];
        if (fieldData && fieldData.editable) {
          const currentValue = formValues[fieldKey];
          const originalValue = fieldData.value;
          
          // Only include if value has actually changed
          if (currentValue !== originalValue) {
            updateData[fieldKey] = currentValue;
          }
        }
      });
      
      if (Object.keys(updateData).length === 0) {
        setError('No actual changes detected');
        setSaving(false);
        return;
      }
      
      console.log('💾 Saving inventory updates:', updateData);
      
      const response = await fetch(`${config.apiUrl}/api/inventory/update/${encodeURIComponent(circuitId.trim())}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Successfully updated ${result.result.updated_fields} fields!`);
        setHasChanges(false);
        
        // Refresh the data to show updated values
        await handleSearch();
      } else {
        setError(`Save failed: ${result.error}`);
      }
      
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      console.error('❌ Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Render field function
  const renderField = (fieldKey, fieldData) => {
    if (!fieldData || typeof fieldData !== 'object') return null;
    
    const value = formValues[fieldKey] || '';
    const isEditable = fieldData.editable;
    
    return (
      <Grid item xs={12} sm={6} md={4} key={fieldKey}>
        <Box>
          <TextField
            fullWidth
            size="small"
            label={fieldData.display_label}
            value={value}
            onChange={(e) => handleValueChange(fieldKey, e.target.value)}
            disabled={!isEditable}
            type={fieldData.data_type === 'number' ? 'number' : 'text'}
            variant={isEditable ? 'outlined' : 'filled'}
            InputProps={{
              readOnly: !isEditable,
            }}
            sx={{
              '& .MuiFilledInput-root': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiInputLabel-root': {
                color: isEditable ? 'primary.main' : 'text.secondary',
              }
            }}
            helperText={
              fieldData.validation?.min !== undefined && fieldData.validation?.max !== undefined
                ? `Range: ${fieldData.validation.min} - ${fieldData.validation.max}`
                : fieldData.validation?.max_length
                ? `Max length: ${fieldData.validation.max_length}`
                : isEditable ? 'Editable field' : 'Read-only field'
            }
          />
        </Box>
      </Grid>
    );
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InventoryIcon sx={{ fontSize: 32, color: '#003781', mr: 2 }} />
              <Typography variant="h5" sx={{ color: '#003781', fontWeight: 'bold' }}>
                Inventory Management
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search and update circuit inventory data directly in the ODM/TEMP database
            </Typography>
            
            {/* User Access Badge */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip 
                label={`${userRole.roleName} Access`}
                color="success" 
                variant="outlined"
                size="small"
              />
              <Chip 
                label={userRole.userEmail}
                color="primary" 
                variant="outlined"
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Circuit ID"
                value={circuitId}
                onChange={(e) => setCircuitId(e.target.value)}
                placeholder="Enter Circuit ID (e.g., GMPLS6C-01011776-0009)"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || !circuitId.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ backgroundColor: '#003781' }}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </CardContent>
        </Card>

        {/* Circuit Data Display */}
        {circuitData && (
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#003781' }}>
                  Circuit: {circuitId}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {hasChanges && (
                    <Chip 
                      label="Unsaved Changes"
                      color="warning" 
                      variant="outlined"
                    />
                  )}
                  <Chip 
                    label={`${Object.keys(circuitData).filter(key => 
                      circuitData[key]?.editable
                    ).length} Editable Fields`}
                    variant="outlined" 
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {Object.keys(circuitData).map(fieldKey => {
                  if (fieldKey === 'circuit_id') return null;
                  return renderField(fieldKey, circuitData[fieldKey]);
                })}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Floating Save Button */}
        {hasChanges && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              backgroundColor: '#003781',
            }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </Fab>
        )}
      </Container>
    </Layout>
  );
};

export default InventoryManagementPage;